import React, { useEffect, useRef, useState } from 'react';
import DatePicker, { DateObject, DatePickerRef } from 'react-multi-date-picker';
import 'react-multi-date-picker/styles/colors/red.css';
import Icon from 'react-multi-date-picker/components/icon';
import { CrossIcon, dayjs, FORMAT_DATE_API } from '@umanforge/common';
import { Button } from 'components/inputs/button.component';
import { useTranslation } from 'react-i18next';

interface DatePickerSoloInputProp {
  label?: string;
  placeholder?: string;
  labelPosition?: 'top' | 'left';
  textSize?: string;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  startDate?: dayjs.Dayjs | null;
  defaultValue?: dayjs.Dayjs | null;
  disable?: boolean;
  endDate?: dayjs.Dayjs | null;
  clearAfrerClose?: boolean;
  shadow?: string;
  border?: string;
  width?: string;
  dateFormat?: string;
  onChange?: ( date: dayjs.Dayjs | undefined ) => void;
  onClose?: ( date: dayjs.Dayjs | undefined ) => void;
  calendarPosition?: 'top' | 'bottom'
  canCancel?: boolean
  canBeNull?: boolean
}

const DatePickerSoloInput: React.FC<DatePickerSoloInputProp> = ( {
  label,
  placeholder,
  labelPosition = 'left',
  disable,
  textSize = 'w-3/12 mr-4',
  width = 'w-full',
  minDate,
  maxDate,
  defaultValue,
  clearAfrerClose,
  dateFormat = 'LL',
  onChange,
  onClose,
  calendarPosition = 'top',
  canCancel = false,
  canBeNull = false
} ) => {
  const pickerRef = useRef();
  const localDayjs = dayjs.Ls[ dayjs.locale() ];
  const { t } = useTranslation();

  const [ date, setDate ] = useState<dayjs.Dayjs | null>( defaultValue ?? null );
  const [ max, setMax ] = useState<dayjs.Dayjs | undefined>( maxDate ? dayjs( maxDate ).startOf( 'day' ) : undefined );
  const [ min, setMin ] = useState<dayjs.Dayjs | undefined>( minDate ? dayjs( minDate ).startOf( 'day' ) : undefined );
  const [ isOpen, setIsOpen ] = useState( false );
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  useEffect( () => {
    if ( !isOpen ) {
      if ( onClose && date ) {
        onClose( date );
      }
      if ( clearAfrerClose ) {
        setDate( null );
      }
    }
  }, [ isOpen ] );

  useEffect( () => {
    if ( isFirstRender ) {
      setIsFirstRender( false );
    }
  }, [ ] );

  useEffect( () => {
    if ( isFirstRender || date == null ) {
      setDate( defaultValue ?? null );
    }
  }, [ defaultValue ] );

  useEffect( () => {
    if ( minDate ) {
      setMin( dayjs( minDate ).startOf( 'day' ) );
    } else {
      setMin( undefined );
    }
  }, [ minDate ] );

  useEffect( () => {
    if ( maxDate ) {
      setMax( dayjs( maxDate ).startOf( 'day' ) );
    } else {
      setMax( undefined );
    }
  }, [ maxDate ] );

  const handleChange = ( dateObjects: DateObject | dayjs.Dayjs ) => {
    setDate( dayjs( dateObjects.format( FORMAT_DATE_API ) ) );

    if ( onChange ) {
      onChange( dayjs( dateObjects.format( FORMAT_DATE_API ) ) );
    }
  };

  const handleCancel = ( event: React.MouseEvent<HTMLDivElement, MouseEvent> ) => {
    if ( canCancel && onChange ) {
      event.stopPropagation();

      // if date already same, set to undefined
      if ( canBeNull && defaultValue?.diff( date, 'day' ) == 0 ) {
        onChange( undefined );
        setDate( null );
      }
      else {
        onChange( defaultValue ?? undefined );
        setDate( defaultValue ?? null );
      }
    }
  };

  function TodayButton( { }: { position: string } ) {
    return <>
      <Button
        onClick={ () => { handleChange( dayjs() ); }}
        label={ t( 'today' ) }
        color='#0074d9'
        padding={0}
      />
    </>;
  }

  return (
    <div className={`rounded-md ${width} ${labelPosition === 'left' ? 'flex' : ''}`}>
      {label && (
        <div className={`flex gap-2 relative items-center ${textSize}`}>
          <p className={`text-gray-600 text-wrap break-words ${labelPosition === 'left' ? 'flex items-center' : ''}`}>
            {label}
          </p>
        </div>
      )}
      <div className={`relative w-full ${labelPosition === 'top' ? 'mt-2' : ''} flex items-center`}>
        {/* Input style */}
        <DatePicker
          disabled={disable}
          ref={pickerRef}
          zIndex={1000}
          minDate={min?.toDate()}
          maxDate={max?.toDate()}
          placeholder={placeholder}
          value={date ? date.toDate() : undefined }
          onChange={ dateObj => { if ( dateObj ) { handleChange( dateObj ); } } }
          onOpen={() => {return setIsOpen( true );}}
          onClose={() => {return setIsOpen( false );}}
          showOtherDays
          render={<input
            className={`pl-4 p-2 w-full shadow min-h-11 outline-none rounded-md border-[1px] ${disable ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'} hover:border-black focus:border-black focus:ring-1 focus:ring-black`}
            disabled={disable}
            value={date ? dayjs( date ).format( dateFormat ) : ''}
            placeholder={placeholder}
            readOnly
          />}
          weekStartDayIndex={ localDayjs.weekStart }
          weekDays={ localDayjs.weekdaysShort }
          months={ localDayjs.months }
          format={ localDayjs.formats.L }
          editable={!disable}
          name={label}
          calendarPosition={calendarPosition}
          plugins={[ <TodayButton key={'today_button'} position='bottom'/> ]}
          portal
        />
        { !disable ?
          <div
            className='h-full px-2 cursor-pointer absolute right-0 top-0 flex items-center'
            onClick={() => {
              const ref = ( pickerRef.current as unknown as DatePickerRef | null );
              if ( ref ) {
                if ( isOpen ) {
                  ref.closeCalendar();
                } else {
                  ref.openCalendar();
                }
              }
            }}
          >
            <Icon className='h-full p-0.5' />

            { canCancel &&
              <div onClick={ e => { handleCancel( e ); } }>
                <CrossIcon className='text-red-600 hover:text-red-800' />
              </div>
            }
          </div>
          :
          <div className='h-full px-2 absolute cursor-not-allowed right-0 top-0 flex items-center' >
            <Icon className='h-full p-0.5' color='#747a87' cursor={'not-allowed'}/>
          </div>
        }
      </div>
    </div>
  );
};

export default DatePickerSoloInput;
